import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Shield, MessageCircle, Users } from "lucide-react";

const LEGAL_DISCLAIMER = "Este servicio ofrece acompañamiento emocional y no sustituye atención psicológica profesional.";

export default function Landing() {
  return (
    <Layout showFooter={true}>
      {/* Hero Section */}
      <section className="min-h-[85vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-primary/5"></div>
        
        <div className="container-main relative z-10 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
              Encuentra a alguien que te <span className="text-primary">escuche</span>
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-relaxed max-w-2xl mx-auto">
              Conecta con personas reales para una conversación sincera. No es terapia, es <strong>alguien que te escucha</strong>.
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/explorar">
                <Button 
                  className="btn-primary text-lg px-10 py-6 shadow-lg shadow-primary/30 hover:shadow-xl transition-all"
                  data-testid="cta-explorar"
                >
                  Explorar listeners
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            {/* Trust signals */}
            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-stone-500">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                100% Confidencial
              </span>
              <span className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-primary" />
                Sesiones de 50 min
              </span>
              <span className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary" />
                Sin juicios
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 lg:py-24 bg-stone-50">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
              ¿Cómo funciona?
            </h2>
            <p className="mt-4 text-stone-600">
              En 3 pasos estarás hablando con alguien que te escucha
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Elige a tu listener",
                description: "Mira los videos de presentación y elige a quien más conecte contigo",
                icon: Users
              },
              {
                step: "2", 
                title: "Agenda tu sesión",
                description: "Escoge el horario que te funcione mejor y paga de forma segura",
                icon: Shield
              },
              {
                step: "3",
                title: "Habla y desahógate",
                description: "50 minutos de conversación privada por videollamada",
                icon: MessageCircle
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100 text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto">
                  {item.step}
                </div>
                <h3 className="mt-6 text-xl font-semibold text-foreground">{item.title}</h3>
                <p className="mt-3 text-stone-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What This IS and IS NOT */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground text-center mb-12">
              ¿Qué es Apoyum?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
                <h3 className="text-xl font-semibold text-green-800 mb-6">
                  ✓ Esto SÍ es Apoyum
                </h3>
                <ul className="space-y-3 text-green-700">
                  <li>• Alguien que te escucha sin juzgar</li>
                  <li>• Conversaciones reales y sinceras</li>
                  <li>• Un espacio para desahogarte</li>
                  <li>• Escucha humana y cercana</li>
                </ul>
              </div>
              
              <div className="bg-stone-50 rounded-2xl p-8 border border-stone-200">
                <h3 className="text-xl font-semibold text-stone-700 mb-6">
                  Esto NO es Apoyum
                </h3>
                <ul className="space-y-3 text-stone-600">
                  <li>— No es terapia psicológica</li>
                  <li>— No es atención médica</li>
                  <li>— No damos diagnósticos</li>
                  <li>— No sustituye profesionales</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
            A veces solo necesitas que alguien te escuche
          </h2>
          <p className="mt-4 text-xl text-white/80 max-w-2xl mx-auto">
            Encuentra a tu listener hoy.
          </p>
          <div className="mt-8">
            <Link to="/explorar">
              <Button 
                className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl"
                data-testid="cta-final"
              >
                Ver listeners disponibles
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 bg-stone-100 border-t border-stone-200">
        <div className="container-main">
          <p className="text-center text-sm text-stone-500 max-w-3xl mx-auto">
            <strong>Aviso importante:</strong> {LEGAL_DISCLAIMER}
          </p>
        </div>
      </section>
    </Layout>
  );
}
